/**
 * WorkItem — Individual work item card with expand/collapse and response forms
 */
import React, { useState } from 'react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  MessageSquare, FileText, Check, Loader2, User, AlertCircle,
  ChevronDown, Eye, Send, Clock, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { ITEM_TYPE_ICONS } from './constants';
import { LabResponseForm, TaskActionBar, ItemStatusBadge } from './WorkItemForms';

export function WorkItem({ item, bucket, expanded, onToggle, onComplete }) {
  const [response, setResponse] = useState('');
  const [routing, setRouting] = useState('internal_note');
  const [notes, setNotes] = useState('');
  const [aiSummary, setAiSummary] = useState(item.ai_summary || '');
  const [aiLoading, setAiLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const TypeIcon = ITEM_TYPE_ICONS[item.item_type] || FileText;

  const handleAiSummarize = async () => {
    setAiLoading(true);
    try {
      const res = await api.post(`/my-day/items/${item.id}/ai-summary`);
      setAiSummary(res.data.summary || '');
    } catch (err) {
      console.error('AI summary failed:', err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRespond = async () => {
    if (!response.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/my-day/items/${item.id}/respond`, {
        response_text: response,
        routing,
      });
      onComplete();
    } catch (err) {
      console.error('Respond failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcknowledge = async () => {
    // Legacy — now uses file_item
    setSubmitting(true);
    try {
      await api.post(`/my-day/items/${item.id}/file`, { notes });
      onComplete();
    } catch (err) {
      console.error('File failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`
        rounded-xl border transition-all duration-150
        ${expanded ? 'border-border shadow-md bg-card' : 'border-border/60 bg-card hover:border-border hover:shadow-sm'}
      `}
      data-testid={`work-item-${item.id}`}
    >
      {/* Item header — always visible */}
      <button
        onClick={onToggle}
        className="w-full text-left px-4 py-3.5 flex items-center gap-3"
        data-testid={`work-item-toggle-${item.id}`}
      >
        <div className="p-1.5 rounded-lg bg-muted flex-shrink-0">
          <TypeIcon className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <User className="w-3 h-3" />
              {item.patient_name}
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {_formatTime(item.created_at)}
            </span>
          </div>
        </div>
        <Badge variant="outline" className="text-[10px] flex-shrink-0">
          {item.item_type?.replace(/_/g, ' ')}
        </Badge>
        <ItemStatusBadge status={item.status} />
        <ArrowRight className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-border/50" data-testid={`work-item-detail-${item.id}`}>
          {/* Thread history */}
          {(item.thread || []).length > 0 && (
            <div className="mt-3 mb-3 space-y-1.5" data-testid={`thread-${item.id}`}>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Thread</p>
              {item.thread.map((entry, i) => (
                <div key={entry.id || i} className={`rounded-md px-2.5 py-1.5 text-xs ${
                  entry.author_role === 'provider' ? 'bg-primary/5 border border-primary/10 ml-4' : 'bg-muted/50 border border-border/50 mr-4'
                }`}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="font-medium text-[10px]">{entry.author_name || entry.author_role}</span>
                    <span className="text-[9px] text-muted-foreground">{_formatTime(entry.timestamp)}</span>
                    {entry.action_type && (
                      <Badge variant="outline" className="text-[8px] h-3.5 px-1">{entry.action_type.replace(/_/g, ' ')}</Badge>
                    )}
                  </div>
                  <p className="text-foreground leading-relaxed">{entry.message}</p>
                </div>
              ))}
            </div>
          )}

          {/* AI Summary section */}
          <div className="mt-3 mb-3">
            {aiSummary ? (
              <div className="rounded-lg bg-blue-50/50 border border-blue-100/60 px-3 py-2.5" data-testid={`ai-summary-${item.id}`}>
                <p className="text-xs font-medium text-blue-600/80 mb-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> AI Summary
                </p>
                <p className="text-sm text-foreground leading-relaxed">{aiSummary}</p>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAiSummarize}
                disabled={aiLoading}
                className="text-xs"
                data-testid={`ai-summarize-btn-${item.id}`}
              >
                {aiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Generate AI Summary
              </Button>
            )}
          </div>

          {/* Content body */}
          <div className="rounded-lg bg-muted/40 px-3 py-2.5 mb-3" data-testid={`item-body-${item.id}`}>
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{item.body}</p>
          </div>

          {/* Action section — differs by bucket */}
          {bucket === 'lab_xray' ? (
            <LabResponseForm
              response={response}
              setResponse={setResponse}
              routing={routing}
              setRouting={setRouting}
              onSubmit={handleRespond}
              submitting={submitting}
              itemId={item.id}
            />
          ) : (
            <TaskActionBar
              item={item}
              notes={notes}
              setNotes={setNotes}
              submitting={submitting}
              setSubmitting={setSubmitting}
              onComplete={onComplete}
              bucket={bucket}
            />
          )}
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   LAB RESPONSE FORM
   ═══════════════════════════════════════ */

